import { ComponentCO2Link } from '@acies/domain';
import { ObservableCollection } from '@acies/observables';
import { CheckBoxEvent } from '@acies/ui';
import NetworkError from '@shared/errors/network-error';
import CO2Model from '../models/co2-model';
import DeleteComponentCO2LinkModel from '../models/delete-component-co2link-model';
import ListComponentCO2LinkModel from '../models/list-component-co2link-model';
import CO2Service from '../services/co2-service';

export default class DeleteComponentCO2LinkController {
	public dialog: HTMLDialogElement | null;
	#deleteComponentCO2LinkModel: DeleteComponentCO2LinkModel;
	#listComponentCO2LinkModel: ListComponentCO2LinkModel;
	#co2Service: CO2Service;
	#co2LinkCollection: ObservableCollection<ComponentCO2Link>;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.dialog = null;
		this.#deleteComponentCO2LinkModel = co2Model.deleteComponentCO2LinkModel;
		this.#listComponentCO2LinkModel = co2Model.listComponentCO2LinkModel;
		this.#co2Service = co2Service;
		this.#co2LinkCollection = this.#listComponentCO2LinkModel.co2LinkCollection;
		this.co2LinkCollectionChanged = this.co2LinkCollectionChanged.bind(this);
		this.#co2LinkCollection.subscribe(this.co2LinkCollectionChanged);
		this.deleteNextComponentCO2Link = this.deleteNextComponentCO2Link.bind(this);
	}

	private co2LinkCollectionChanged(): void {
		this.#deleteComponentCO2LinkModel.itemsToBeDeleted.clear();
		this.#updateDeleteButtonEnabled();
		this.#deleteComponentCO2LinkModel.selectAllCheckBoxSelected = false;
		this.#updateSelectAllCheckBoxEnabled();
	}

	public selectAllComponentCO2CheckBoxChanged(event: Event): void {
		if (event instanceof CheckBoxEvent) {
			if (event.selected) {
				this.#selectAllCheckBoxSelected();
			} else {
				this.#selectAllCheckBoxUnSelected();
			}
			// syncronize the model with the event selected property
			this.#deleteComponentCO2LinkModel.selectAllCheckBoxSelected = event.selected;
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
			this.#hideAllErrorLabels();
		}
	}

	public dialogDeleteButtonClicked(): void {
		this.#deleteComponentCO2LinkModel.dialogCancelButtonEnabled = false;
		this.#deleteComponentCO2LinkModel.dialogDeleteButtonEnabled = false;
		this.#deleteComponentCO2LinkModel.deleteSpinnerVisible = true;
		this.#co2LinkCollection.unsubscribe(this.co2LinkCollectionChanged);
		this.deleteNextComponentCO2Link();
	}

	public deleteCheckBoxToogle(event: Event): void {
		if (event instanceof CheckBoxEvent && event.item instanceof ComponentCO2Link) {
			if (event.selected) {
				this.#deleteComponentCO2LinkModel.itemsToBeDeleted.addItem(event.item);
			} else {
				this.#deleteComponentCO2LinkModel.itemsToBeDeleted.removeItem(event.item);
			}
			this.#updateDeleteButtonEnabled();
			// any of the items are toggled, we unselect the select all checkbox
			this.#deleteComponentCO2LinkModel.selectAllCheckBoxSelected = false;
		}
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	private deleteNextComponentCO2Link(): void {
		this.#hideAllErrorLabels();
		const componentCO2Link = this.#deleteComponentCO2LinkModel.itemsToBeDeleted.getItemAt(0);
		// if componentCO2Link is not null, we can safely remove
		// it from the Collection and delete it on the server.
		if (componentCO2Link) {
			this.#co2Service.deleteComponentCO2Link(componentCO2Link).subscribe((response) => {
				if (response instanceof ComponentCO2Link) {
					this.#deleteComponentCO2LinkModel.itemsToBeDeleted.removeItem(componentCO2Link);
					this.#co2LinkCollection.removeItem(componentCO2Link);
					this.deleteNextComponentCO2Link();
				} else {
					// if we encounter an error during batch deletion, give the user,
					// the chance to continue with deletion, if for example, the error
					// was a temporary NetworkError.
					this.#co2LinkCollection.subscribe(this.co2LinkCollectionChanged);
					this.#deleteComponentCO2LinkModel.dialogCancelButtonEnabled = true;
					this.#deleteComponentCO2LinkModel.dialogDeleteButtonEnabled = true;
					this.#deleteComponentCO2LinkModel.deleteSpinnerVisible = false;
					this.#showErrorLabel(response);
				}
			});
		} else {
			// if componentCO2Link is null, we are done deleting.
			this.#co2LinkCollection.subscribe(this.co2LinkCollectionChanged);
			this.#deleteComponentCO2LinkModel.dialogCancelButtonEnabled = true;
			this.#deleteComponentCO2LinkModel.dialogDeleteButtonEnabled = true;
			this.#deleteComponentCO2LinkModel.deleteSpinnerVisible = false;
			this.#updateDeleteButtonEnabled();
			this.closeDialog();
			this.#updateSelectAllCheckBoxEnabled();
		}
	}

	#hideAllErrorLabels(): void {
		this.#deleteComponentCO2LinkModel.networkErrorLabelVisible = false;
		this.#deleteComponentCO2LinkModel.unknownErrorLabelVisible = false;
	}

	#showErrorLabel(error: NetworkError): void {
		if (error instanceof NetworkError) {
			this.#deleteComponentCO2LinkModel.networkErrorLabelVisible = true;
		} else {
			this.#deleteComponentCO2LinkModel.unknownErrorLabelVisible = true;
		}
	}

	#updateDeleteButtonEnabled(): void {
		this.#deleteComponentCO2LinkModel.deleteButtonEnabled = this.#deleteComponentCO2LinkModel.itemsToBeDeleted.length > 0;
	}

	#updateSelectAllCheckBoxEnabled(): void {
		// the co2LinkCollection.collection contains the filtered items, so use that to update enabled true / false
		this.#listComponentCO2LinkModel.selectAllCheckBoxEnabled = this.#co2LinkCollection.collection.length > 0;
	}

	#selectAllCheckBoxSelected(): void {
		this.#deleteComponentCO2LinkModel.itemsToBeDeleted.clear();
		for (const item of this.#co2LinkCollection.collection) {
			item.selected.value = false;
			item.selected.value = true;
			this.#deleteComponentCO2LinkModel.itemsToBeDeleted.addItem(item);
		}
		this.#updateDeleteButtonEnabled();
	}

	#selectAllCheckBoxUnSelected(): void {
		this.#deleteComponentCO2LinkModel.itemsToBeDeleted.clear();
		for (const item of this.#listComponentCO2LinkModel.co2LinkCollection.collection) {
			item.selected.value = true;
			item.selected.value = false;
		}
		this.#updateDeleteButtonEnabled();
	}
}
